import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_ASSIGNMENT_LIST_VIEWS, ASSIGNMENT_STATUS } from '@lims/shared-types';
import {
  assignmentsApi,
  type Assignment,
  type AssignmentWorkOrderGroup,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  PaginationNav,
  Panel,
  SearchField,
  SearchSelectField,
  type SelectOption,
} from '../../components/ui/index.js';
import { formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { readOpenGroups, rememberOpenGroup } from '../samples/open-groups.js';
import { groupBySample, groupByWorkOrder, WorkTable } from './grouped-work.js';
import { ViewTabPanel, ViewTabs } from './work-ui.js';

/**
 * Work orders per page (D326). Each carries up to `GROUP_ITEM_LIMIT` of its
 * lab work, so a page is bounded however large one work order grows.
 */
const GROUPS_PER_PAGE = 10;
const GROUP_ITEM_LIMIT = 50;

/**
 * The laboratory work list, as Work Order → Sample → Tests (D326).
 *
 * Every follow-up view is filtered by the BACKEND, so the counts describe the
 * rows. "Delayed" shows the original due date beside the latest expected date
 * and the recorded reason, because a delay that overwrote the promise would
 * hide the fact that one was broken.
 *
 * GROUPED BY WORK ORDER. The server pages work orders, newest first, with the
 * work of samples linked to none last ("Without Work Order"). Inside a group
 * the newest sample comes first and each sample's tests in the order they
 * were assigned. This page only cuts that order where the work order and the
 * sample change, so a sample is named once, its tests under it, and never
 * re-sorts it. A status view, the search and the work order filter narrow the
 * work itself: a sample or a work order with nothing matching is absent.
 *
 * A group with more than `GROUP_ITEM_LIMIT` matching items shows that many and
 * offers the rest in the group itself, continuing in the same order.
 *
 * OPEN AND CLOSED GROUPS are remembered in this browser, per person, never in
 * the database (`open-groups.ts`): with no choice remembered the newest work
 * order is open and the rest closed. While a search, a view or one work order
 * is shown, every matching group opens, and a choice made then lasts for that
 * result only.
 */
export function WorkListPage() {
  const { t } = useTranslation();
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const view = params.get('view') ?? 'all';
  // The work order filter, in the address: one work order's id, or `none`.
  // `job` is its number, for display only.
  const workOrderFilter = params.get('workOrderId');
  const workOrderLabel = params.get('job');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // The filters a group's "Show more" repeats, so the rest of a group is the
  // same selection as its first rows.
  const filterQuery: Record<string, string> = { view, ...(search ? { search } : {}) };
  const filterKey = { page, workOrderFilter, ...filterQuery };
  const list = useQuery({
    queryKey: ['assignments', 'by-work-order', filterKey],
    queryFn: () =>
      assignmentsApi.list({
        page,
        pageSize: GROUPS_PER_PAGE,
        pageBy: 'workOrder',
        groupItemLimit: GROUP_ITEM_LIMIT,
        ...(workOrderFilter ? { workOrderId: workOrderFilter } : {}),
        ...filterQuery,
      }),
  });

  const storageKey = `lims.work.openGroups:${user?.tenant?.slug ?? ''}:${user?.id ?? ''}`;
  const [remembered, setRemembered] = useState(() => readOpenGroups(storageKey));
  const opensMatches = Boolean(search || workOrderFilter || view !== 'all');
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const listKey = JSON.stringify(filterKey);
  useEffect(() => setOpened({}), [listKey]);
  const isGroupOpen = (key: string, index: number) =>
    opensMatches ? (opened[key] ?? true) : (remembered[key] ?? index === 0);
  const toggleGroup = (key: string, index: number) => {
    const next = !isGroupOpen(key, index);
    if (opensMatches) setOpened((current) => ({ ...current, [key]: next }));
    else setRemembered(rememberOpenGroup(storageKey, key, next));
  };

  const chooseWorkOrder = (id: string | null, number?: string) => {
    const next = new URLSearchParams(params);
    next.delete('workOrderId');
    next.delete('job');
    if (id !== null) {
      next.set('workOrderId', id);
      if (number) next.set('job', number);
    }
    setParams(next);
    setPage(1);
  };

  const rows = list.data?.data ?? [];
  const isFiltered = Boolean(search || workOrderFilter || view !== 'all');

  return (
    <>
      <PageHeader
        title={t('work.title')}
        subtitle={t('work.subtitle')}
        breadcrumbs={[{ label: t('nav.laboratoryWork') }]}
        actions={
          can(PERMISSIONS.ASSIGNMENTS_CREATE) ? (
            <ActionButton priority="primary" onPress={() => navigate('/work/assign')}>
              {t('work.assignTest')}
            </ActionButton>
          ) : null
        }
      />

      {/* The views the old "Show" select offered, as tabs: the same URL
          parameter and the same backend-filtered request. */}
      <ViewTabs
        label={t('work.viewLabel')}
        idBase="work-view"
        value={view}
        tabs={ALL_ASSIGNMENT_LIST_VIEWS.map((value) => ({
          value,
          label: t(`assignmentView.${value}`),
        }))}
        onChange={(value) => {
          const next = new URLSearchParams(params);
          next.set('view', value);
          setParams(next);
          setPage(1);
        }}
      />

      <div className="mt-stack">
        <ViewTabPanel idBase="work-view" value={view}>
          <div className="flex flex-col gap-stack">
            <FilterBar>
              <SearchField
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setPage(1);
                }}
                label={t('work.searchLabel')}
                placeholder={t('work.searchPlaceholder')}
              />
              <WorkOrderFilter
                value={workOrderFilter}
                valueLabel={workOrderLabel}
                onChange={chooseWorkOrder}
              />
            </FilterBar>

            {list.error ? (
              <ErrorState error={list.error} onRetry={() => void list.refetch()} />
            ) : list.isLoading ? (
              <LoadingState />
            ) : rows.length === 0 ? (
              <Panel>
                <EmptyState title={isFiltered ? t('work.noneFound') : t('work.noneYet')} />
              </Panel>
            ) : (
              <div className="flex flex-col gap-stack" role="list" aria-label={t('work.groupsLabel')}>
                {groupByWorkOrder(rows, list.data?.meta.workOrders ?? []).map(
                  ({ group, rows: groupRows }, index) => {
                    const key = group.id ?? 'none';
                    return (
                      <WorkOrderGroup
                        key={key}
                        group={group}
                        rows={groupRows}
                        filterQuery={filterQuery}
                        pageLimit={list.data?.meta.groupItemLimit ?? GROUP_ITEM_LIMIT}
                        isOpen={isGroupOpen(key, index)}
                        onToggle={() => toggleGroup(key, index)}
                      />
                    );
                  },
                )}
                <PaginationNav
                  page={page}
                  totalPages={list.data?.meta.totalPages ?? 1}
                  onPageChange={setPage}
                />
              </div>
            )}
          </div>
        </ViewTabPanel>
      </div>
    </>
  );
}

/**
 * The work order filter: all, one work order, or the work of samples with
 * none. The list is this laboratory's work orders that have lab work, searched
 * on the server as the person types, so it scales to any number.
 */
function WorkOrderFilter({
  value,
  valueLabel,
  onChange,
}: {
  readonly value: string | null;
  readonly valueLabel: string | null;
  readonly onChange: (id: string | null, number?: string) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  // One request per pause in typing, not one per key.
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(text.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [text]);
  const found = useQuery({
    queryKey: ['assignment-work-order-options', search],
    queryFn: () => assignmentsApi.workOrderOptions({ ...(search ? { search } : {}), limit: 20 }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const workOrders = found.data?.data ?? [];
  const options: SelectOption[] = [
    { value: ALL_WORK_ORDERS, label: t('work.workOrderFilterAll') },
    { value: 'none', label: t('work.withoutWorkOrder') },
    // A work order chosen from a link stays listed while other searches run.
    ...(value && value !== 'none' && !workOrders.some((row) => row.id === value)
      ? [{ value, label: valueLabel ?? t('work.workOrderFilter') }]
      : []),
    ...workOrders.map((row) => {
      const parties = [row.clientName, row.projectName].filter(Boolean).join(' / ');
      return parties
        ? { value: row.id, label: row.number, description: parties }
        : { value: row.id, label: row.number };
    }),
  ];

  return (
    <SearchSelectField
      label={t('work.workOrderFilter')}
      value={value}
      onChange={(next) => {
        if (next === ALL_WORK_ORDERS) onChange(null);
        else if (next === 'none') onChange('none');
        else onChange(next, workOrders.find((row) => row.id === next)?.number ?? valueLabel ?? undefined);
      }}
      options={options}
      onSearchChange={setText}
      {...(valueLabel ? { selectedLabel: valueLabel } : {})}
      placeholder={t('work.workOrderFilterPlaceholder')}
      emptyContent={t('work.workOrderFilterEmpty')}
      isLoading={found.isFetching}
    />
  );
}

/** The filter's "all work orders" row; never a record id. */
const ALL_WORK_ORDERS = '__all__';

const STATUS_ORDER: readonly string[] = Object.values(ASSIGNMENT_STATUS);

function WorkOrderGroup({
  group,
  rows: firstRows,
  filterQuery,
  pageLimit,
  isOpen,
  onToggle,
}: {
  readonly group: AssignmentWorkOrderGroup;
  readonly rows: readonly Assignment[];
  readonly filterQuery: Record<string, string>;
  readonly pageLimit: number;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();
  const bodyId = useId();
  const name = group.id ? (group.number ?? '') : t('work.withoutWorkOrder');
  const parties = [group.clientName, group.projectName].filter(Boolean).join(' / ');
  // The rest of a large group: the ordinary paging of this one work order in
  // the grouped order, continuing where the page stops.
  const [wantsMore, setWantsMore] = useState(false);
  const more = useInfiniteQuery({
    queryKey: ['assignments', 'group-rest', group.id ?? 'none', pageLimit, filterQuery],
    queryFn: ({ pageParam }) =>
      assignmentsApi.list({
        workOrderId: group.id ?? 'none',
        sort: 'workOrder',
        page: pageParam,
        pageSize: pageLimit,
        ...filterQuery,
      }),
    initialPageParam: 2,
    getNextPageParam: (last) =>
      last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined,
    enabled: wantsMore,
  });
  const extra = more.data?.pages.flatMap((response) => response.data) ?? [];
  const rows = [...firstRows, ...extra.filter((row) => !firstRows.some((first) => first.id === row.id))];
  const total = Math.max(group.itemCount, rows.length);
  const hasMore = rows.length < total && (!more.data || more.hasNextPage);
  const sampleTotal = Math.max(group.sampleCount, groupBySample(rows).length);
  const summary = [
    t('work.groupSamples', { total: formatNumber(sampleTotal, { locale }) }),
    t('work.groupItems', { total: formatNumber(total, { locale }) }),
    ...STATUS_ORDER.filter((status) => (group.statusCounts[status] ?? 0) > 0).map(
      (status) =>
        `${t(`status.test.${status}`)}: ${formatNumber(group.statusCounts[status] ?? 0, { locale })}`,
    ),
  ];

  return (
    <section
      role="listitem"
      className="min-w-0 rounded-large border border-default-200 bg-white"
      data-testid="work-group"
      data-work-order={group.id ?? 'none'}
    >
      <header className="flex min-w-0 items-start gap-2 px-3 py-2.5 sm:px-4">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          aria-label={t('work.groupToggle', { name })}
          className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-medium text-default-700 hover:bg-default-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
        >
          <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            className={`size-4 transition-transform ${isOpen ? '' : '-rotate-90 rtl:rotate-90'}`}
          >
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-body font-bold text-default-900">
            {group.id && can(PERMISSIONS.WORK_ORDERS_VIEW) ? (
              <Link
                to={`/work-orders/${group.id}`}
                dir="ltr"
                className="measure underline-offset-2 hover:text-primary-700 hover:underline"
                data-testid="work-group-link"
              >
                {name}
              </Link>
            ) : (
              <span {...(group.id ? { dir: 'ltr', className: 'measure' } : {})}>{name}</span>
            )}
            {parties ? (
              <span className="min-w-0 truncate font-normal text-default-700">{parties}</span>
            ) : null}
          </h2>
          <p className="text-caption text-default-600" data-testid="work-group-summary">
            {summary.join(' · ')}
            {rows.length < total
              ? ` · ${t('work.groupShowing', {
                  shown: formatNumber(rows.length, { locale }),
                  total: formatNumber(total, { locale }),
                })}`
              : null}
          </p>
        </div>
      </header>
      <div id={bodyId} hidden={!isOpen} className="border-t border-default-200 px-1 pb-1 sm:px-2">
        {isOpen ? (
          <>
            <WorkTable label={name} rows={rows} />
            {hasMore ? (
              <div className="flex justify-center px-2 py-2">
                <ActionButton
                  priority="tertiary"
                  size="sm"
                  isLoading={more.isFetching}
                  onPress={() => {
                    if (!wantsMore) setWantsMore(true);
                    else void more.fetchNextPage();
                  }}
                >
                  {t('work.groupShowMore')}
                </ActionButton>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
